import {
  BatchUpdateResults,
  FormProofValues,
  FormProofValuesUpdate,
  type Proof,
  ProofDetails,
  Question,
  QuestionDetails,
  QuestionOption,
  QuestionStorePayload,
  QuestionUpdatePayload,
  Res,
} from '@/types'

import base from './base'

export const exerciseApi2 = base.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    getAllQuestions: builder.query<Question[], void>({
      query: () => 'teachers/questions',
      transformResponse: (res: Res<Question[]>) => res.data,
      providesTags: ['ExerciseQuestions'],
    }),
    addNewQuestion: builder.mutation<Question, QuestionStorePayload>({
      query: (body) => ({
        url: 'teachers/questions',
        method: 'POST',
        body,
      }),
      transformResponse: (res: Res<Question>) => res.data,
      invalidatesTags: (_, error) => (!error ? ['ExerciseQuestions'] : []),
    }),
    updateQuestionOption: builder.mutation<void, QuestionOption>({
      query: ({ id, ...body }) => ({
        url: `teachers/options/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, error, { id }) =>
        !error ? [{ type: 'ExerciseQuestions', id }] : [],
    }),
    deleteQuestionOption: builder.mutation<void, number>({
      query: (id) => ({
        url: `teachers/options/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, id) =>
        !error ? [{ type: 'ExerciseQuestions', id }] : [],
    }),
    getAllProofs: builder.query<Proof[], void>({
      query: () => 'teachers/exams',
      transformResponse: (res: Res<Proof[]>) => res.data,
      providesTags: ['ExerciseProofs'],
    }),
    createProof: builder.mutation<Proof, FormProofValues>({
      query: (body) => ({
        url: 'teachers/exams',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ExerciseProofs'],
    }),
    updateProof: builder.mutation<Proof, FormProofValuesUpdate>({
      query: ({ id, ...body }) => ({
        url: `teachers/exams/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['ExerciseProofs'],
    }),
    getProofDetails: builder.query<ProofDetails, string>({
      query: (id) => `teachers/exams/${id}`,
      providesTags: (_, __, id) => [{ type: 'ExerciseProofs', id }],
      transformResponse: (res: Res<ProofDetails>) => res.data,
    }),
    deleteProof: builder.mutation<void, number>({
      query: (id) => ({
        url: `teachers/exams/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ExerciseProofs'],
    }),
    batchUpdateResults: builder.mutation<void, BatchUpdateResults[]>({
      query: (body) => ({
        url: 'teachers/results/batch-update',
        method: 'PUT',
        body: { results: body },
      }),
      invalidatesTags: ['ExerciseProofs'],
    }),
    getQuestionDetails: builder.query<QuestionDetails, number>({
      query: (id) => `teachers/questions/${id}`,
      providesTags: (_, __, id) => [{ type: 'ExerciseQuestions', id }],
      transformResponse: (res: Res<QuestionDetails>) => res.data,
    }),
    getQuestionContent: builder.query<QuestionDetails, number>({
      query: (id) => `teachers/questions/${id}/content`,
      transformResponse: (res: Res<QuestionDetails>) => res.data,
    }),
    getQuestionOptions: builder.query<QuestionOption[], number>({
      query: (id) => `teachers/questions/${id}/options`,
      transformResponse: (res: Res<QuestionOption[]>) => res.data,
    }),
    updateQuestion: builder.mutation<void, QuestionUpdatePayload>({
      query: ({ id, ...body }) => ({
        url: `teachers/questions/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_, error) => (!error ? ['ExerciseQuestions'] : []),
    }),
    removeQuestion: builder.mutation<void, number>({
      query: (id) => ({
        url: `teachers/questions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error) => (!error ? ['ExerciseQuestions'] : []),
    }),
  }),
})

export const {
  useGetAllQuestionsQuery,
  useAddNewQuestionMutation,
  useUpdateQuestionOptionMutation,
  useDeleteQuestionOptionMutation,
  useLazyGetProofDetailsQuery,
  useGetAllProofsQuery,
  useDeleteProofMutation,
  useCreateProofMutation,
  useUpdateProofMutation,
  useBatchUpdateResultsMutation,
  useLazyGetQuestionDetailsQuery,
  useLazyGetQuestionOptionsQuery,
  useRemoveQuestionMutation,
  useUpdateQuestionMutation,
  useLazyGetQuestionContentQuery,
} = exerciseApi2
